import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Text } from '../../components/ui'
import {
  DASHBOARD_TOOLTIP_CONTENT_STYLE,
  DASHBOARD_TOOLTIP_ITEM_STYLE,
  DASHBOARD_TOOLTIP_LABEL_STYLE,
} from '../../components/centralAmparo/dashboardHelpers'
import type {
  SupportCenterStrategicMetrics,
  SupportCenterStrategicRate,
  SupportCenterVolumeByCatalog,
} from '../../types/supportCenterReports'

type DashboardIndicadoresSectionProps = {
  metrics: SupportCenterStrategicMetrics
}

function formatCount(value: number): string {
  return value.toLocaleString('pt-BR')
}

function formatRate(rate: SupportCenterStrategicRate): string {
  if (rate.rate == null) return '—'
  return `${rate.rate.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
}

function formatRatio(rate: SupportCenterStrategicRate): string {
  if (rate.denominator <= 0) return 'Sem base no período'
  return `${formatCount(rate.numerator)} de ${formatCount(rate.denominator)}`
}

function KpiCard({
  label,
  value,
  hint,
  gradient,
}: {
  label: string
  value: string
  hint?: string
  gradient: string
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} shadow-lg rounded-lg p-4 text-white`}>
      <div className="text-center">
        <Text size={value.length > 14 ? 'lg' : '2xl'} className="text-white font-bold tabular-nums mb-1">
          {value}
        </Text>
        <Text size="sm" className="text-white">
          {label}
        </Text>
        {hint ? (
          <Text size="xs" className="text-white/80 mt-1">
            {hint}
          </Text>
        ) : null}
      </div>
    </div>
  )
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <Text size="sm" className="text-gray-800 dark:text-gray-100 font-medium">
        {title}
      </Text>
      <Text size="xs" color="muted">
        {subtitle}
      </Text>
    </div>
  )
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
      <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">
        {title}
      </Text>
      <Text size="xs" color="muted" className="mb-3">
        {subtitle}
      </Text>
      {children}
    </div>
  )
}

function EmptyChart() {
  return (
    <div className="h-72 flex items-center justify-center">
      <Text size="sm" color="muted">
        Sem dados para o período.
      </Text>
    </div>
  )
}

function HorizontalBarChart({
  data,
  seriesName,
}: {
  data: Array<{ name: string; total: number }>
  seriesName: string
}) {
  if (data.length === 0 || data.every((item) => item.total === 0)) return <EmptyChart />
  const chartHeight = Math.max(280, data.length * 36)
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
        <YAxis type="category" dataKey="name" width={148} tick={{ fontSize: 11 }} interval={0} />
        <Tooltip
          contentStyle={DASHBOARD_TOOLTIP_CONTENT_STYLE}
          labelStyle={DASHBOARD_TOOLTIP_LABEL_STYLE}
          itemStyle={DASHBOARD_TOOLTIP_ITEM_STYLE}
        />
        <Bar dataKey="total" fill="#008B95" name={seriesName} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function topRows(rows: SupportCenterVolumeByCatalog[], limit = 12) {
  return rows.slice(0, limit).map((row) => ({ name: row.name, total: row.total }))
}

const DashboardIndicadoresSection: React.FC<DashboardIndicadoresSectionProps> = ({ metrics }) => {
  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Concentração por profissional"
          subtitle="Atendimentos realizados no período"
        >
          <HorizontalBarChart data={topRows(metrics.by_professional)} seriesName="Realizados" />
        </ChartCard>
        <ChartCard title="Faixa etária" subtitle="Pacientes distintos, idade na data da consulta">
          <HorizontalBarChart data={topRows(metrics.by_age_band, 8)} seriesName="Pacientes" />
        </ChartCard>
      </div>

      <section className="space-y-4">
        <SectionTitle
          title="Perda e retrabalho"
          subtitle="Reagendamento é a parcela de pacientes com ao menos um horário substituído. Cancelamento em cima da hora conta o cancelamento feito até 24 h antes do horário."
        />
        <div className="grid grid-cols-2 gap-3">
          <KpiCard
            label="Reagendamento"
            value={formatRate(metrics.reschedule_rate)}
            hint={formatRatio(metrics.reschedule_rate)}
            gradient="from-amber-500 to-amber-600"
          />
          <KpiCard
            label="Cancelamento em cima da hora"
            value={formatRate(metrics.last_minute_cancel_rate)}
            hint={formatRatio(metrics.last_minute_cancel_rate)}
            gradient="from-red-500 to-red-600"
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Motivo de cancelamento" subtitle="Agendamentos cancelados no período">
            <HorizontalBarChart data={topRows(metrics.cancel_by_reason)} seriesName="Cancelados" />
          </ChartCard>
          <ChartCard title="Canal do cancelamento" subtitle="Canal registrado no cancelamento">
            <HorizontalBarChart data={topRows(metrics.cancel_by_channel)} seriesName="Cancelados" />
          </ChartCard>
        </div>
      </section>
    </div>
  )
}

export default DashboardIndicadoresSection
